import { createTheme, responsiveFontSizes } from "@mui/material/styles";

const sharedTypography = {
  fontFamily: [
    "Inter",
    '"Segoe UI"',
    "Roboto",
    '"Helvetica Neue"',
    "Arial",
    "sans-serif",
  ].join(","),
  h1: { fontWeight: 600, letterSpacing: "-0.02em" },
  h2: { fontWeight: 600, letterSpacing: "-0.015em" },
  h3: { fontWeight: 600, letterSpacing: "-0.01em" },
  h4: { fontWeight: 600, letterSpacing: "-0.01em" },
  h5: { fontWeight: 600 },
  h6: { fontWeight: 600 },
  body1: { lineHeight: 1.55 },
  body2: { lineHeight: 1.5 },
  button: { fontWeight: 600, textTransform: "none" },
};

const buildComponentOverrides = (theme) => ({
  MuiAppBar: {
    styleOverrides: {
      root: {
        backdropFilter: "blur(10px)",
        backgroundColor:
          theme.palette.mode === "dark"
            ? "rgba(17, 24, 36, 0.92)"
            : "rgba(255, 255, 255, 0.92)",
        backgroundImage:
          theme.palette.mode === "dark"
            ? "linear-gradient(180deg, rgba(24, 33, 48, 0.98) 0%, rgba(17, 24, 36, 0.92) 100%)"
            : "linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(249, 251, 255, 0.94) 100%)",
        color: theme.palette.text.primary,
        borderBottom: `1px solid ${theme.palette.divider}`,
        boxShadow:
          theme.palette.mode === "dark"
            ? "0 10px 24px rgba(0, 0, 0, 0.24)"
            : "0 6px 18px rgba(15, 23, 42, 0.05)",
      },
    },
  },
  MuiDrawer: {
    styleOverrides: {
      paper: {
        borderRight: `1px solid ${theme.palette.divider}`,
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundImage: "none",
      },
      rounded: {
        borderRadius: 12,
      },
    },
  },
  MuiCard: {
    defaultProps: {
      elevation: 0,
    },
    styleOverrides: {
      root: {
        border: `1px solid ${theme.palette.divider}`,
        boxShadow:
          theme.palette.mode === "dark"
            ? "0 6px 20px rgba(0, 0, 0, 0.3)"
            : "0 6px 20px rgba(15, 23, 42, 0.08)",
      },
    },
  },
  MuiButton: {
    defaultProps: {
      disableElevation: true,
      size: "medium",
    },
    styleOverrides: {
      root: {
        borderRadius: 8,
        paddingInline: 14,
      },
    },
  },
  MuiTextField: {
    defaultProps: {
      size: "small",
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: 8,
      },
    },
  },
  MuiDialog: {
    defaultProps: {
      fullWidth: true,
      maxWidth: "sm",
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: {
        fontWeight: 650,
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: 999,
        fontWeight: 500,
      },
    },
  },
  MuiTabs: {
    styleOverrides: {
      root: {
        minHeight: 42,
      },
      indicator: {
        height: 3,
        borderRadius: 3,
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        minHeight: 42,
        fontWeight: 600,
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: {
        borderBottom: `1px solid ${theme.palette.divider}`,
      },
      head: {
        fontWeight: 650,
        backgroundColor: theme.palette.mode === "dark" ? "#202734" : "#eef2ff",
      },
      sizeSmall: {
        paddingTop: 8,
        paddingBottom: 8,
      },
    },
  },
  MuiAlert: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        border: `1px solid ${theme.palette.divider}`,
      },
      standardSuccess: {
        backgroundColor: theme.palette.mode === "dark" ? "#1a3a23" : "#e8f7ee",
        color: theme.palette.mode === "dark" ? "#d5f4de" : "#145a32",
      },
      standardError: {
        backgroundColor: theme.palette.mode === "dark" ? "#41212a" : "#fdecee",
        color: theme.palette.mode === "dark" ? "#ffd6de" : "#7b1d2a",
      },
      standardWarning: {
        backgroundColor: theme.palette.mode === "dark" ? "#3e3016" : "#fff7e6",
        color: theme.palette.mode === "dark" ? "#ffe4b2" : "#7a4b00",
      },
      standardInfo: {
        backgroundColor: theme.palette.mode === "dark" ? "#162f49" : "#e8f2ff",
        color: theme.palette.mode === "dark" ? "#cfe8ff" : "#0d3f7a",
      },
    },
  },
  MuiTooltip: {
    defaultProps: {
      enterTouchDelay: 0,
    },
  },
});

const buildTheme = (mode) => {
  const isDark = mode === "dark";
  const base = createTheme({
    palette: {
      mode,
      primary: {
        main: isDark ? "#7fc2ff" : "#145da0",
        light: isDark ? "#b7ddff" : "#4e8bc8",
        dark: isDark ? "#4d96d8" : "#0e3f6d",
        contrastText: isDark ? "#031628" : "#ffffff",
      },
      secondary: {
        main: isDark ? "#f4b567" : "#c77700",
        light: isDark ? "#ffd599" : "#db9739",
        dark: isDark ? "#c9862f" : "#8a5200",
        contrastText: isDark ? "#2b1800" : "#ffffff",
      },
      background: {
        default: isDark ? "#0b1118" : "#fcfdff",
        paper: isDark ? "#131c28" : "#ffffff",
      },
      text: {
        primary: isDark ? "#f2f6fb" : "#122033",
        secondary: isDark ? "#b7c6d8" : "#4c607a",
      },
      divider: isDark ? "rgba(167, 188, 214, 0.22)" : "rgba(18, 32, 51, 0.12)",
      action: {
        hover: isDark ? "rgba(127, 194, 255, 0.1)" : "rgba(20, 93, 160, 0.08)",
        selected: isDark
          ? "rgba(127, 194, 255, 0.16)"
          : "rgba(20, 93, 160, 0.14)",
      },
      error: { main: "#d64550" },
      warning: { main: "#c77700" },
      info: { main: isDark ? "#7fc2ff" : "#1665b3" },
      success: { main: "#2f9e64" },
    },
    shape: {
      borderRadius: 10,
    },
    spacing: 8,
    typography: sharedTypography,
  });

  return responsiveFontSizes(
    createTheme(base, {
      components: buildComponentOverrides(base),
    }),
    { factor: 2.1 },
  );
};

export const lightTheme = buildTheme("light");
export const darkTheme = buildTheme("dark");
